import React, { useState, useRef, useEffect } from 'react';
import './CustomSelect.css';

export interface Option {
  value: string;
  label: string;
  icon?: string;
  group?: string;
}

interface CustomSelectProps {
  id?: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
}

export function CustomSelect({
  id,
  value,
  options,
  onChange,
  placeholder = 'Select an option...',
  disabled = false,
  label
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-unused-vars
  const [_portalAnchor, _setPortalAnchor] = useState<DOMRect | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group options if they have a group property
  const groupedOptions = filteredOptions.reduce((acc, opt) => {
    const group = opt.group || '';
    if (!acc[group]) acc[group] = [];
    acc[group].push(opt);
    return acc;
  }, {} as Record<string, Option[]>);

  const hasGroups = Object.keys(groupedOptions).length > 1 || Object.keys(groupedOptions)[0] !== '';

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Only auto-focus on desktop (768px and up)
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      if (isDesktop) {
        searchInputRef.current.focus();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="custom-select-wrapper" ref={dropdownRef}>
      {label && <label className="custom-select-label">{label}</label>}
      <button
        id={id}
        type="button"
        className={`custom-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="custom-select-value">
          {selectedOption ? selectedOption.label : <span className="placeholder">{placeholder}</span>}
        </span>
        <span className="custom-select-arrow">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="custom-select-dropdown">
          {options.length > 5 && (
            <div className="custom-select-search">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <ul className="custom-select-options" role="listbox">
            {filteredOptions.length === 0 ? (
              <li className="custom-select-option empty">No results found</li>
            ) : hasGroups ? (
              Object.entries(groupedOptions)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([group, groupOptions]) => (
                  <li key={group} className="custom-select-group">
                    {group && <div className="custom-select-group-label">{group}</div>}
                    <ul className="custom-select-group-options">
                      {groupOptions.map(option => (
                        <li
                          key={option.value}
                          className={`custom-select-option ${option.value === value ? 'selected' : ''}`}
                          onClick={() => handleSelect(option.value)}
                          role="option"
                          aria-selected={option.value === value}
                        >
                          <span className="option-content">
                            {option.label}
                          </span>
                          {option.value === value && (
                            <span className="option-checkmark">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))
            ) : (
              filteredOptions.map(option => (
                <li
                  key={option.value}
                  className={`custom-select-option ${option.value === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                  role="option"
                  aria-selected={option.value === value}
                >
                  <span className="option-content">
                    {option.label}
                  </span>
                  {option.value === value && (
                    <span className="option-checkmark">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
